/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Capacitor } from "@capacitor/core";
import { Health as CapGoHealth } from "@capgo/capacitor-health";
import { UnifiedHealthService } from "./health-unified";
import {
  getPlatformConfig,
  getEnabledDataTypes,
  getReadPermissions,
} from "../config/health-data-config";

import { apiClient } from "./api";
import firebaseAnalytics from "./firebaseAnalytics";
import { getIonicStorage, ionicStorage } from "./storageService";
import { Storage } from "@ionic/storage";
import { Device } from "@capacitor/device";
import { AppLauncher } from "@capacitor/app-launcher";
import { Preferences } from "@capacitor/preferences";

/**
 * Ensures health service is available on the device
 * For Android: Redirects to Play Store if Health Connect not installed
 * For iOS: Checks if HealthKit is available
 *
 * @param platform - The platform to check ("ios" | "android")
 * @returns Result with availability status, success flag, and message
 */
export const ensureHealthServiceAvailable = async (
  platform: "ios" | "android"
): Promise<{ available: boolean; success: boolean; message: string }> => {
  try {
    const isAvailable = await UnifiedHealthService.isAvailable();

    if (isAvailable) {
      return {
        available: true,
        success: true,
        message: "Health service is available"
      };
    }

    // Not available - handle based on platform
    if (platform === "android") {
      // Redirect to Play Store to install Health Connect
      console.log("Health Connect not installed, redirecting to Play Store...");

      try {
        // Open Play Store directly via HTTPS URL (avoids app selector drawer on some devices)
        const playStoreUrl = "https://play.google.com/store/apps/details?id=com.google.android.apps.healthdata";
        await AppLauncher.openUrl({ url: playStoreUrl });

        return {
          available: false,
          success: false,
          message: "Health Connect not installed. Redirecting to Play Store. Please install and try again."
        };
      } catch (error) {
        console.error("Error opening Play Store:", error);
        return {
          available: false,
          success: false,
          message: "Health Connect not installed. Please install it manually from Play Store."
        };
      }
    } else {
      // iOS - HealthKit should always be available
      return {
        available: false,
        success: false,
        message: "HealthKit is not available on this device"
      };
    }
  } catch (error) {
    console.error("Error checking health service availability:", error);
    return {
      available: false,
      success: false,
      message: error instanceof Error ? error.message : "Unknown error checking health service"
    };
  }
};

/**
 * Open health settings for the user to manage permissions
 * iOS: Opens Apple Health app
 * Android: Opens Health Connect settings (or redirects to Play Store if not installed)
 */
export const openHealthSettings = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const platform = Capacitor.getPlatform();

    if (platform === "ios") {
      // Open Apple Health app using URL scheme
      try {
        const { completed } = await AppLauncher.openUrl({
          url: "x-apple-health://"
        });

        if (completed) {
          return {
            success: true,
            message: "Opened Apple Health app"
          };
        } else {
          return {
            success: false,
            message: "Could not open Apple Health app"
          };
        }
      } catch (error) {
        console.error("Error opening Apple Health:", error);
        return {
          success: false,
          message: "Apple Health app not available"
        };
      }
    } else if (platform === "android") {
      // First check if Health Connect is available (installed)
      const isAvailable = await UnifiedHealthService.isAvailable();

      if (!isAvailable) {
        // Not installed - redirect to Play Store
        console.log("Health Connect not installed, redirecting to Play Store...");
        try {
          // Open Play Store directly via HTTPS URL (avoids app selector drawer on some devices)
          const playStoreUrl = "https://play.google.com/store/apps/details?id=com.google.android.apps.healthdata";
          await AppLauncher.openUrl({ url: playStoreUrl });
          return {
            success: true,
            message: "Redirected to Play Store to install Health Connect"
          };
        } catch (error) {
          console.error("Error opening Play Store:", error);
          return {
            success: false,
            message: "Health Connect not installed. Please install from Play Store."
          };
        }
      }

      // Health Connect is installed - open settings
      try {
        await CapGoHealth.openHealthConnectSettings();
        return {
          success: true,
          message: "Opened Health Connect settings"
        };
      } catch (error) {
        console.error("Error opening Health Connect settings:", error);
        return {
          success: false,
          message: "Could not open Health Connect settings"
        };
      }
    } else {
      return {
        success: false,
        message: "Unsupported platform"
      };
    }
  } catch (error) {
    console.error("Error opening health settings:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Unknown error"
    };
  }
};

/**
 * Check if health service is available on the current platform
 * UPDATED: Now uses UnifiedHealthService
 */
export const AvailableHealthServices = async (): Promise<string[]> => {
  const availableServices: string[] = [];
  const platform = Capacitor.getPlatform();

  try {
    const isAvailable = await UnifiedHealthService.isAvailable();
    if (isAvailable) {
      if (platform === "ios") {
        availableServices.push("HealthKit");
      } else if (platform === "android") {
        availableServices.push("Health Connect");
      }
    }
  } catch (error) {
    console.error("Health service not available:", error);
  }

  return availableServices;
};

/**
 * Check if health service is available on the current platform
 * UPDATED: Now uses UnifiedHealthService
 */
export const checkPlatformAvailability = async (): Promise<boolean> => {
  try {
    return await UnifiedHealthService.isAvailable();
  } catch (error) {
    console.error("Error checking health service availability:", error);
    return false;
  }
};

/**
 * Request health data permissions using UnifiedHealthService
 * Platform-agnostic implementation using @capgo/capacitor-health
 */
const requestUnifiedPermissions = async (
  platform: "ios" | "android"
): Promise<{ success: boolean; message: string }> => {
  try {
    console.log(`Requesting health permissions for ${platform}...`);

    // Check if health service is available first
    const isAvailable = await UnifiedHealthService.isAvailable();
    if (!isAvailable) {
      return {
        success: false,
        message: `Health service not available on ${platform}`,
      };
    }

    // Request permissions through unified service
    const result = await UnifiedHealthService.requestPermissions(platform);

    if (result.granted) {
      console.log(
        `✅ Permissions granted for ${result.dataTypes?.length || 0} data types`
      );

      return {
        success: true,
        message: `Health permissions granted successfully for ${platform}`,
      };
    } else {
      return {
        success: false,
        message: result.error || "Permissions not granted",
      };
    }
  } catch (error) {
    console.error("Error requesting health permissions:", error);
    return {
      success: false,
      message:
        error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

const getHealthSyncCoolDown = async (): Promise<number> => {
  const info = await Device.getInfo();

  if (info.platform === "android") {
    const androidVersion = parseInt(info.osVersion.split(".")[0]);
    if (androidVersion <= 13) {
      console.log(
        "Android version:",
        androidVersion,
        "- applying 15 minute cooldown for Health Connect rate limiting"
      );
      return 15 * 60 * 1000; // 15 minutes for Android ≤13
    }
  }

  // No timeout for iOS and Android >13
  return 0;
};

const LAST_FETCH_KEY = "lastHealthDataFetch"; // Match health-unified.ts

/**
 * Check if enough time has passed since last fetch
 */
const canFetchHealthData = async (storage: Storage | null): Promise<boolean> => {
  const lastFetchTime = await getIonicStorage(storage, LAST_FETCH_KEY);
  if (!lastFetchTime) return true;

  const timeSinceLastFetch = Date.now() - parseInt(lastFetchTime, 10);

  return timeSinceLastFetch >= (await getHealthSyncCoolDown());
};

/**
 * Update last fetch timestamp
 */
const updateLastFetchTime = async (storage: Storage | null): Promise<void> => {
  await ionicStorage(storage, LAST_FETCH_KEY, Date.now().toString());
};


// ============================================================================
// DIAGNOSTIC FUNCTIONS - REMOVED DURING MIGRATION
// TODO: Move to test file (Phase 7 - LOW priority per plan)
// These used old @perfood/capacitor-healthkit plugin APIs
// Not used in production - only for debugging/testing
// Removed functions:
// - fetchLast7DaysHealthData()
// - prepareDataForServerSync()
// - fetchHealthKitData()
// - diagnoseSleepDataIssues()
// - diagnoseStepsDataIssues()
// - diagnoseHeartRateDataIssues()
// ============================================================================
// Note: UUID generation is handled by UnifiedHealthService.convertToServerFormat()
// using the generateUUID function in health-unified.ts
// ============================================================================

/**
 * Process and upload health data to server
 * UPDATED: Now uses UnifiedHealthService for all platforms
 */
export const processAndUploadHealthData = async (
  storage: Storage | null
) => {
  try {
    console.log("Starting health data processing...");

    const platform = Capacitor.getPlatform() as "ios" | "android";
    console.log(`Platform detected: ${platform}`);

    // Validate platform
    if (platform !== "ios" && platform !== "android") {
      return {
        success: false,
        message: "Unsupported platform",
      };
    }

    // Check rate limiting before proceeding
    const canFetch = await canFetchHealthData(storage);
    if (!canFetch) {
      // Rate limit only applies to Android ≤13 (always 15 minutes)
      console.log(`Rate limit: Please wait 15 minutes between syncs`);
      return {
        success: false,
        message: `Please wait 15 minutes between health data syncs`,
      };
    }

    // Calculate date range - last 7 days
    // Fetch for 9 days: one day before and one day after the last 7 days
    // Using device's local timezone to ensure complete day coverage
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 1); // One day after today
    endDate.setHours(23, 59, 59, 999); // End of day in local timezone

    const startDate = new Date(endDate); // Copy endDate to preserve correct date
    startDate.setDate(startDate.getDate() - 9); // 9 days before endDate
    startDate.setHours(0, 0, 0, 0); // Start of day in local timezone

    console.log(
      `Fetching health data from ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`
    );
    console.log(`User timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`);

    // Check if health service is available and redirect to Play Store if needed (Android)
    const availabilityResult = await ensureHealthServiceAvailable(platform);

    if (!availabilityResult.available) {
      // Not available - user has been redirected to Play Store (Android) or received error (iOS)
      console.log(`Health service not available: ${availabilityResult.message}`);
      return {
        success: false,
        message: availabilityResult.message,
      };
    }

    console.log("Health service available");

    // Request authorization using unified service
    // Note: Users control access through OS permissions (iOS Health / Android Health Connect)
    console.log("Requesting health data authorization...");

    const authResult = await requestUnifiedPermissions(platform);
    if (!authResult.success) {
      console.error("Failed to get permissions:", authResult.message);
      return {
        success: false,
        message: "Failed to get health permissions: " + authResult.message,
      };
    }

    console.log("Authorization granted. Fetching health data...");

    // Fetch profile_id from Preferences to prevent cross-user data collisions
    const { value: userDataStr } = await Preferences.get({ key: "user" });
    if (!userDataStr) {
      console.error("User data not found in Preferences");
      return {
        success: false,
        message: "User not authenticated",
      };
    }

    let profileId: number;
    try {
      const userData = JSON.parse(userDataStr);
      profileId = userData.profile_id;

      if (!profileId) {
        console.error("profile_id not found in user data");
        return {
          success: false,
          message: "Invalid user data - profile_id missing",
        };
      }

      console.log(`Using profile_id: ${profileId} for health data sync`);
    } catch (error) {
      console.error("Error parsing user data from Preferences:", error);
      return {
        success: false,
        message: "Failed to parse user data",
      };
    }

    // Fetch health data using unified service
    const healthData = await UnifiedHealthService.fetchHealthData(
      platform,
      startDate,
      endDate
    );

    if (!healthData || Object.keys(healthData).length === 0) {
      console.log("No health data available to upload");
      return { success: true, message: "No data to upload" };
    }

    console.log(
      `Health data fetched successfully: ${Object.keys(healthData).length} data types`
    );

    // Convert health data to server format using unified service
    const events = UnifiedHealthService.convertToServerFormat(
      platform,
      healthData,
      profileId
    );

    if (events.length === 0) {
      console.log("No events to upload after conversion");
      return { success: true, message: "No events to upload" };
    }

    console.log("=".repeat(80));
    console.log(`📤 UPLOADING TO SERVER`);
    console.log("=".repeat(80));
    console.log(`Total events: ${events.length}`);

    // Create the request payload
    const payload = {
      events: events,
      options: {
        batchSize: 50,
        returnRecords: false,
      },
    };

    // Send data to server using apiClient
    const response = await apiClient.post(
      "/api/activity-events/upload",
      payload
    );

    // Log the success
    console.log("\n" + "=".repeat(80));
    console.log("✅ UPLOAD SUCCESSFUL");
    console.log("=".repeat(80));
    console.log("Server response:", {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
    });
    console.log("=".repeat(80));
    console.log("\n");


    // Update last fetch timestamp for rate limiting
    await updateLastFetchTime(storage);
    console.log("Rate limit timestamp updated");

    return { success: true, data: response.data };
  } catch (error) {
    console.error("Error in processAndUploadHealthData:", error);
    firebaseAnalytics.logEvent("health_data_sync_error", {
      error: error instanceof Error ? error.message : "Unknown error",
      platform: Capacitor.getPlatform(),
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Request health permissions explicitly - PLATFORM AWARE
 * UPDATED: Now uses UnifiedHealthService
 * After requesting permissions, automatically opens health settings so users can manage them
 * @param permissionsToRequest Optional array of specific permissions (currently ignored, uses config)
 * @returns Object with success status and message
 */
export const requestHealthKitPermissions = async (
  _permissionsToRequest?: string[]
): Promise<{ success: boolean; message: string }> => {
  try {
    const platform = Capacitor.getPlatform() as "ios" | "android";
    console.log(`Requesting permissions for platform: ${platform}`);

    // Validate platform
    if (platform !== "ios" && platform !== "android") {
      return { success: false, message: "Unsupported platform" };
    }

    // Check availability first and redirect to Play Store if needed (Android)
    const availabilityResult = await ensureHealthServiceAvailable(platform);
    if (!availabilityResult.available) {
      // Not available - user has been redirected to Play Store (Android) or received error (iOS)
      return {
        success: false,
        message: availabilityResult.message,
      };
    }

    // Request permissions using unified service
    const result = await requestUnifiedPermissions(platform);

    if (result.success) {
      console.log("Permissions granted, opening health settings for user to review...");
    }

    return result;
  } catch (error) {
    console.error("Error requesting health permissions:", error);
    return {
      success: false,
      message:
        error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

// Api to upload health data to the server
export const uploadHealthDataToServer = async (
  events: Record<string, any>
): Promise<{ success: boolean; data?: any; message?: string; error?: any }> => {
  try {
    console.log("Uploading health data to server...");
    if (events.length === 0) {
      console.log("No events to upload after conversion");
      return { success: true, message: "No events to upload" };
    }

    console.log(`Uploading ${events.length} events to server...`);

    // Create the request payload
    const payload = {
      events: events,
      options: {
        batchSize: 50,
        returnRecords: false,
      },
    };

    // Send data to server using your apiClient
    const response = await apiClient.post(
      "/api/activity-events/upload",
      payload
    );

    // Log the success
    console.log("Health data uploaded successfully:", response.data);


    return { success: true, data: response.data };
  } catch (error) {
    console.error("Error in uploadHealthDataToServer:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};
